import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import { AuthState, LoginFormInputs } from "../types";
import {
  getClientID,
  setAuthToken,
  removeAuthToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken,
  persistCognitoUsernameForRecovery,
} from "../utils/authUtils";

const BaseURL = import.meta.env.VITE_API_URL;

export const login = createAsyncThunk<any, LoginFormInputs>(
  "user/login",
  async ({ username, password, recaptchaToken }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(`${BaseURL}/api/v1/auth/signin`, {
        email: username,
        password,
        recaptchaToken,
      });
      // return result.data;
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during login"
      );
    }
  }
);

// ログインユーザの権限・能力（/auth/me）を取得する。
// capabilities.actions を can() 判定に使う（防御の本体は API 側）。
export const fetchMe = createAsyncThunk<{
  actions: string[];
  options: string[];
}>("user/fetchMe", async (_arg, { rejectWithValue }) => {
  try {
    // /auth/me は物件管理BEから取得（BEが共有DBを読む。設定BEはサービスから叩かない）。
    const result = await ipponApi.get(`${BaseURL}/api/v1/auth/me`);
    const data = result?.data?.data;
    return {
      actions: data?.capabilities?.actions ?? [],
      options: data?.features?.options_enabled ?? [],
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "An error occurred while fetching /auth/me"
    );
  }
});

export const checkMail = createAsyncThunk<any, any>(
  "user/checkMail",
  async ({ email }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(`${BaseURL}/api/v1/auth/check-email`, {
        email,
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during check mail"
      );
    }
  }
);

export const requestOtp = createAsyncThunk<any, any>(
  "user/requestOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(`${BaseURL}/api/v1/auth/otp`, {
        email,
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during request otp"
      );
    }
  }
);

export const verifyOtp = createAsyncThunk<any, any>(
  "user/verifyOtp",
  async ({ otp, email }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(`${BaseURL}/api/v1/auth/verify-otp`, {
        otp,
        email,
      });
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during verify otp"
      );
    }
  }
);

export const forgotPassword = createAsyncThunk<any, any>(
  "user/forgotPassword",
  async ({ newPassword, email, verificationCode }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(
        `${BaseURL}/api/v1/auth/reset-password`,
        {
          newPassword,
          email,
          verificationCode,
        }
      );
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during reset password"
      );
    }
  }
);

export const changeMemberPassword = createAsyncThunk<any, any>(
  "user/changeMemberPassword",
  async (
    { memberEmail, iv, oldPassword, newPassword },
    { rejectWithValue }
  ) => {
    try {
      const result = await ipponApi.post(
        `${BaseURL}/api/v1/auth/change-password`,
        {
          email: memberEmail,
          iv,
          oldPassword,
          newPassword,
        }
      );
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "An error occurred during change password of member."
      );
    }
  }
);

export const changeMemberPasswordByAdmin = createAsyncThunk<any, any>(
  "user/changeMemberPasswordByAdmin",
  async ({ paramsEmail, oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(
        `${BaseURL}/api/v1/auth/email-change-password`,
        {
          email: paramsEmail,
          oldPassword,
          newPassword,
        }
      );
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "An error occurred during change password of member."
      );
    }
  }
);

export const logoutAPI = createAsyncThunk<any, void>(
  "user/logout",
  async (_, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(`${BaseURL}/api/v1/auth/logout`);
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during logout"
      );
    }
  }
);

// Cognito情報更新時の強制トークンリフレッシュ（forceReauth専用）
// 注意: 通常の期限切れ処理はaxios interceptorで自動実行されます
export const refreshToken = createAsyncThunk<any, void>(
  "user/refreshTokenForCognitoUpdate",
  async (_, { rejectWithValue }) => {
    try {
      const refreshTokenValue = getRefreshToken();

      if (!refreshTokenValue) {
        console.error("[authSlice] Refresh token not found in cookies");
        throw new Error("Refresh token not found");
      }

      // JWTトークンからcognito:usernameを取得
      const { getCognitoUsername } = await import("../utils/authUtils");
      const username = getCognitoUsername();

      if (!username) {
        console.error("[authSlice] Cognito username not found in JWT token");
        throw new Error("Cognito username not found");
      }

      // 循環参照を避けるため、新しいaxiosインスタンスを作成
      const axios = (await import("axios")).default;
      const refreshApi = axios.create({
        baseURL: import.meta.env.VITE_API_URL,
      });

      console.log(
        "[authSlice] Attempting forced token refresh for Cognito update..."
      );
      const result = await refreshApi.post(`/api/v1/auth/refresh-token`, {
        refreshToken: refreshTokenValue,
        username: username,
      });

      console.log("[authSlice] Token refresh successful");
      return result.data;
    } catch (error: any) {
      console.error("[authSlice] Token refresh failed:", {
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        data: error.response?.data,
      });
      return rejectWithValue(
        error.response?.data || "An error occurred during token refresh"
      );
    }
  }
);

export const changeAdminStatus = createAsyncThunk<any, any>(
  "user/changeAdminStatus",
  async ({ member_id, email, newStatus }, { rejectWithValue }) => {
    const clientId = getClientID();
    try {
      const result = await ipponApi.post(
        `${BaseURL}/auth/member-approve?is_approve=${newStatus}`,
        {
          member_id,
          client_id: clientId,
          email,
        }
      );
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during reset password"
      );
    }
  }
);

const initialState: AuthState = {
  loginState: {
    isLoggedIn: false,
    loading: false,
    error: false,
    errorMessage: "",
  },
  user: {
    clientID: "",
    clientName: "",
    employeeID: "",
    role: "",
    token: "",
  },
  redirectPath: null,
  capabilities: [],
  optionsEnabled: [],
  // /auth/me の取得が完了し、UIの権限判定が確定したか。
  // 未取得の間は権限付き操作を表示しない（防御の本体は API 側）。
  capabilitiesLoaded: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logoutLocal: (state) => {
      state.loginState.isLoggedIn = false;
      state.user = {
        clientID: "",
        clientName: "",
        employeeID: "",
        role: "",
        token: "",
      };

      // Clear both token cookies using common utility function
      // This ensures the same options (domain, secure, sameSite) are used as when setting
      removeAuthToken();
      removeRefreshToken();

      // Clear localStorage and sessionStorage for security
      localStorage.clear();
      sessionStorage.clear();
    },
    // トークン切れ時の強制ログアウト用
    forceLogout: (state) => {
      state.loginState.isLoggedIn = false;
      state.user = {
        clientID: "",
        clientName: "",
        employeeID: "",
        role: "",
        token: "",
      };
    },
    setRedirectPath: (state, action) => {
      state.redirectPath = action.payload;
    },
    clearRedirectPath: (state) => {
      state.redirectPath = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action: any) => {
      state.loginState.isLoggedIn = true;
      state.loginState.loading = false;

      // トークンのみを保存（他の情報はJWTに含まれている）
      state.user.token = action.payload.data.data.token;

      // IdTokenとRefreshTokenの両方をCookieに保存
      // 共通関数を使用することで、設定時と削除時のオプションが一致する
      setAuthToken(action.payload.data.data.token.IdToken, 1);
      // [RECOVERY] token 単独消失からの復旧用に username を保持しておく
      persistCognitoUsernameForRecovery();

      // RefreshTokenも保存（期限を長めに設定）
      if (action.payload.data.data.token.RefreshToken) {
        setRefreshToken(action.payload.data.data.token.RefreshToken, 7);
      }
    });
    builder.addCase(fetchMe.fulfilled, (state, action) => {
      state.capabilities = action.payload.actions;
      state.optionsEnabled = action.payload.options;
      state.capabilitiesLoaded = true;
    });
    builder.addCase(fetchMe.pending, (state) => {
      state.capabilities = [];
      state.optionsEnabled = [];
      state.capabilitiesLoaded = false;
    });
    builder.addCase(fetchMe.rejected, (state) => {
      // 取得失敗時は空権限として確定し、UIをfail-closedにする。
      state.capabilities = [];
      state.optionsEnabled = [];
      state.capabilitiesLoaded = true;
    });
    builder.addCase(login.pending, (state) => {
      state.loginState.loading = true;
    });
    builder.addCase(login.rejected, (state) => {
      state.loginState.loading = false;
      state.loginState.error = true;
    });
    builder.addCase(checkMail.fulfilled, () => {});

    // Cognito情報更新時の強制トークンリフレッシュ処理
    builder.addCase(refreshToken.fulfilled, (state, action: any) => {
      // 新しいトークンで更新（Cognito情報の変更を即座に反映）
      state.user.token = action.payload.data.token;

      // 新しいIdTokenをCookieに保存
      // 共通関数を使用することで、設定時と削除時のオプションが一致する
      setAuthToken(action.payload.data.token.IdToken, 1);
      // [RECOVERY] token 単独消失からの復旧用に username を保持しておく
      persistCognitoUsernameForRecovery();

      // 新しいRefreshTokenも保存（返却された場合）
      if (action.payload.data.token.RefreshToken) {
        setRefreshToken(action.payload.data.token.RefreshToken, 7);
      }
    });
    builder.addCase(refreshToken.rejected, (state) => {
      // Cognito情報更新時のリフレッシュ失敗時はログアウト処理を実行
      state.loginState.isLoggedIn = false;
      state.user = {
        clientID: "",
        clientName: "",
        employeeID: "",
        role: "",
        token: "",
      };

      // Clear both token cookies using common utility function
      // This ensures the same options (domain, secure, sameSite) are used as when setting
      removeAuthToken();
      removeRefreshToken();

      // Clear localStorage and sessionStorage for security
      localStorage.clear();
      sessionStorage.clear();
    });

    builder.addCase(logoutAPI.fulfilled, (state) => {
      // API logout成功時にローカルログアウト処理を実行
      state.loginState.isLoggedIn = false;
      state.user = {
        clientID: "",
        clientName: "",
        employeeID: "",
        role: "",
        token: "",
      };
      // 権限は次回ログインで再取得する
      state.capabilities = [];
      state.optionsEnabled = [];
      state.capabilitiesLoaded = false;

      // Clear both token cookies using common utility function
      // This ensures the same options (domain, secure, sameSite) are used as when setting
      removeAuthToken();
      removeRefreshToken();

      // Clear localStorage and sessionStorage for security
      localStorage.clear();
      sessionStorage.clear();
    });
    builder.addCase(logoutAPI.rejected, (state) => {
      // API logout失敗時でもローカルログアウト処理は実行
      state.loginState.isLoggedIn = false;
      state.user = {
        clientID: "",
        clientName: "",
        employeeID: "",
        role: "",
        token: "",
      };

      // Clear both token cookies using common utility function
      // This ensures the same options (domain, secure, sameSite) are used as when setting
      removeAuthToken();
      removeRefreshToken();

      // Clear localStorage and sessionStorage for security
      localStorage.clear();
      sessionStorage.clear();
    });
  },
});

export default authSlice.reducer;

// Export the action creators
export const { logoutLocal, forceLogout, setRedirectPath, clearRedirectPath } =
  authSlice.actions;
